import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useCanEditCompanyDetails } from "@/features/identity/hooks/use-can-edit-company-details";
import { activeSettingsId, buildSettingsNav } from "@/features/settings/settings-nav";
import { useAuth } from "@/shared/auth/auth.context";
import { Permission } from "@/shared/auth/permissions";
import { useCanDo, useCanDoAtAccountLevel } from "@/shared/auth/use-can-do";
import { type ScopeTab, ScopeTabs } from "@/shared/components/scope-tabs/scope-tabs";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

/// The settings surface: a heading, the app's standard horizontal tab strip,
/// and whatever section the caller passes as children.
///
/// A COMPONENT, deliberately, not a route layout. The first version was a
/// layout route, which meant only its own children could render the strip, so
/// the two workspace-scoped sections (which must keep their `/<slug>/...`
/// paths) had to link OUT of it and dropped it entirely, leaving no way back
/// but the browser Back button. As a plain component, every settings route
/// renders the same strip regardless of where its URL lives.
///
/// `ScopeTabs` is the app's established strip (Todos, Feedback, 1:1s,
/// Reviews). It is flat, with no notion of groups, so the account/workspace
/// split this screen spans cannot be shown in the strip itself. That split
/// matters for exactly one reason, knowing WHICH workspace a setting applies
/// to after switching workspaces, so each workspace-scoped section names the
/// workspace in its own header instead (see `sectionSubtitle` below). The
/// signal moved; it did not go away.
export function SettingsLayout({ children }: { children: ReactNode }): ReactNode {
  const { user } = useAuth();
  const { workspace } = useActiveWorkspace();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const canEditCompany = useCanEditCompanyDetails();
  const canEditHealthConfig = useCanDo(Permission.WORKSPACE_HEALTH_CONFIG_EDIT);
  const canManageAiConnections = useCanDoAtAccountLevel(Permission.ACCOUNT_MANAGE_AI_CONNECTIONS);
  const groups = buildSettingsNav({
    workspaceSlug: workspace.slug,
    workspaceName: workspace.name,
    isOwner: user?.accountRole === "owner",
    canEditCompany,
    canEditHealthConfig,
    canManageAiConnections,
  });
  // Flattened: the strip has no grouping, so the groups collapse to one row
  // in declaration order (account sections first, then workspace).
  const items = groups.flatMap((group) => group.items);
  const activeId = activeSettingsId(pathname, groups) ?? items[0]?.id ?? "";

  const tabs: ReadonlyArray<ScopeTab<string>> = items.map((item) => ({
    id: item.id,
    label: item.label,
  }));

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6 md:px-8 md:py-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Settings</h1>

      <ScopeTabs<string>
        scope={activeId}
        tabs={tabs}
        ariaLabel="Settings sections"
        onChange={(next) => {
          const target = items.find((item) => item.id === next);
          if (target) void navigate({ to: target.href });
        }}
      />

      <div className="min-w-0">{children}</div>
    </div>
  );
}
